import React, { memo, ReactElement, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { ThemeProvider } from 'styled-components';

import Breadcrumb from 'components/Breadcrumb';
import EditWrapper from 'components/EditWrapper';
import Hero from 'components/Hero';
import Icon from 'components/Icon';
import PageContent from 'components/PageContent';
import Profile from 'components/Profile';
import RelatedContent from 'components/RelatedContent/RelatedContent';
import TextBlock from 'components/TextBlock';
import { useHeader } from 'hooks';
import useIsInEditMode from 'hooks/useIsInEditMode';
import { Wrapper } from 'style/components/Page';
import { BodyL } from 'style/components/Typography';
import { addEditAttributes } from 'utils/episerver';
import getComponentTypeForContent from 'utils/getComponentTypeForContent';

import {
    Header,
    Container,
    Information,
    TextContent,
    MainContent,
    StartPublish,
    PreHeading,
    Category,
    Introduction,
    MainContentWrapper,
    Navigation,
    NavigationLink,
    NavigationLinkWrapper,
    CategoryLink,
    Authors,
    Length,
    IntroductionHtml,
} from './NewsPage.styled';
import NewsPageProps from './NewsPageProps';

const NewsPage = ({
    heading,
    headingSize = 'XL',
    mainImage,
    breadcrumb,
    preHeading,
    startPublish,
    hideStartPublish,
    categories,
    length,
    introduction,
    introductionHtml,
    video,
    mainBody,
    blocks,
    author,
    mainContent,
    relatedContent,
    theme = 'lightgray',
    contentTheme = 'black',
    actionTheme = 'blue',
    watermark,
    l18n,
}: NewsPageProps): ReactElement => {
    const headerState = useHeader();
    const isInEditMode = useIsInEditMode();
    const [activeIds, setActiveIds] = useState<string[]>([]);

    const newsPageSections = mainContent?.filter((item) => item.component === 'NewsPageSectionBlock');

    const authors = (Array.isArray(author) ? author : [author]).filter(Boolean); // Author can be an array or a single object

    return (
        <Wrapper id="maincontent">
            <Hero image={!video ? mainImage : undefined} video={video} watermark={watermark} {...addEditAttributes('MainImage')} />
            {breadcrumb?.items && <Breadcrumb items={breadcrumb.items} />}
            <Container>
                <ThemeProvider
                    theme={{
                        theme: theme,
                        contentTheme: contentTheme,
                        actionTheme: actionTheme,
                    }}
                >
                    {(isInEditMode || heading) && (
                        <Header key={`NewsPageHeading`} $headingSize={headingSize} {...addEditAttributes('Heading')}>
                            {(isInEditMode || preHeading) && (
                                <PreHeading
                                    key={`NewsPagePreamble`}
                                    {...addEditAttributes('PreHeading')}
                                    dangerouslySetInnerHTML={{
                                        __html: preHeading || '',
                                    }}
                                />
                            )}
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: heading || '',
                                }}
                            />
                        </Header>
                    )}
                    <Information>
                        {(isInEditMode || categories) && (
                            <Category {...addEditAttributes('Category')}>
                                {categories &&
                                    categories.map((category, index, { length }) => (
                                        <span key={`categoryItem${index}`}>
                                            <CategoryLink
                                                key={`categoryItem${index}`}
                                                title={category.displayName}
                                                url={category.url}
                                                hoverSpan={true}
                                            >
                                                <span>{category.displayName}</span>
                                            </CategoryLink>
                                            {index !== length - 1 && ', '}
                                        </span>
                                    ))}
                            </Category>
                        )}
                        {(isInEditMode || startPublish) && !hideStartPublish && (
                            <StartPublish {...addEditAttributes('StartPublish')}>{startPublish}</StartPublish>
                        )}
                        {(isInEditMode || length) && (
                            <Length {...addEditAttributes('Length')}>
                                <Icon icon="clock28" /> {length || ''}
                            </Length>
                        )}
                    </Information>

                    {(isInEditMode || (!introductionHtml && introduction)) && (
                        <Introduction
                            {...addEditAttributes(`Introduction`)}
                            dangerouslySetInnerHTML={{
                                __html: introduction || '',
                            }}
                        />
                    )}
                    {(isInEditMode || introductionHtml) && (
                        <IntroductionHtml editPropertyName={'IntroductionHtml'} content={introductionHtml} renderElement={<BodyL />} />
                    )}
                    {(isInEditMode || (authors && authors.length > 0)) && (
                        <Authors>
                            {authors.map((author, index) => (
                                <Profile
                                    key={`Author${index}`}
                                    fullName={author.fullName}
                                    initials={author.initials}
                                    imageUrl={author.imageUrl}
                                    role={author.role}
                                    url={author.url}
                                    businessArea={author.businessArea}
                                    size="small"
                                    editPropertyName="Author"
                                />
                            ))}
                        </Authors>
                    )}

                    {(isInEditMode || (mainBody && mainBody.length > 0)) && (
                        <TextContent {...addEditAttributes('MainBody')}>
                            <TextBlock
                                content={mainBody}
                                areaName="mainBody"
                                theme={theme}
                                contentTheme={contentTheme}
                                actionTheme={actionTheme}
                                editorialGrid={true}
                                lightbox={true}
                                l18n={l18n}
                            />
                        </TextContent>
                    )}

                    {(isInEditMode || (mainContent && mainContent.length > 0)) && (
                        <MainContentWrapper>
                            {newsPageSections?.length > 0 && (
                                <Navigation $headerVisible={headerState.visible}>
                                    <NavigationLinkWrapper>
                                        {newsPageSections.map((section, index) => {
                                            const identifier = section.anchorName || section.identifier;
                                            return (
                                                <NavigationLink
                                                    key={`NewsPageSectionNav-${index}`}
                                                    to={`#${identifier}`}
                                                    gtmClickType="anchorlink"
                                                    $active={!!identifier && activeIds[activeIds.length - 1] === identifier}
                                                >
                                                    <span>{section.preHeading}</span>
                                                </NavigationLink>
                                            );
                                        })}
                                    </NavigationLinkWrapper>
                                </Navigation>
                            )}
                            <MainContent>
                                {mainContent.map((content, index) => {
                                    if (content.component === 'NewsPageSectionBlock') {
                                        const identifier = content.anchorName || content.identifier;
                                        return (
                                            <Waypoint
                                                key={`wayPoint${index}`}
                                                onEnter={() => {
                                                    setActiveIds(identifier ? [...activeIds, identifier] : activeIds);
                                                }}
                                                onLeave={() => {
                                                    setActiveIds(activeIds.filter((id) => id !== identifier));
                                                }}
                                            >
                                                <div key={`mainContent${index}`}>
                                                    <PageContent
                                                        mainContent={[content]}
                                                        areaName="MainContent"
                                                        componentSelector={getComponentTypeForContent}
                                                    />
                                                </div>
                                            </Waypoint>
                                        );
                                    } else {
                                        return (
                                            <PageContent
                                                key={`mainContent${index}`}
                                                mainContent={[content]}
                                                areaName="MainContent"
                                                componentSelector={getComponentTypeForContent}
                                            />
                                        );
                                    }
                                })}
                            </MainContent>
                        </MainContentWrapper>
                    )}
                </ThemeProvider>
            </Container>

            {(isInEditMode || (blocks && blocks.length > 0)) && (
                <PageContent key={`blocks`} mainContent={blocks} areaName="Blocks" componentSelector={getComponentTypeForContent} />
            )}
            {(isInEditMode || relatedContent) && (
                <EditWrapper editPropertyName="RelatedContent">
                    <RelatedContent key={`relatedContent`} {...relatedContent} />
                </EditWrapper>
            )}
        </Wrapper>
    );
};

export default memo(NewsPage);
